import React, {useState, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {format} from 'date-fns';
import {ContentHeader, Loader} from '@components';
import {logoutUser} from '@app/store/reducers/auth';
import handleStatus from '../../utils/ajax';
import {getAllClient} from '../../services/client';
import {getAllProjectNames} from '../../services/project';
import Pagination from '../../components/pagination/index';
import '../../index.css';
import ProjectDoc from '../docs/ProjectDocs';

const Status = ({status}: {status: boolean}) => {
  if (status) {
    return <span className="badge badge-success">Active</span>;
  }
  return <span className="badge badge-warning">Inactive</span>;
};
const AllClients = () => {
  const [clients, setClients] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [status, setStatus] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [postPerPage] = useState(10);
  const [projects, setProjects] = useState([]);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getAllClients = async (query: any) => {
    setShowLoader(true);
    const res = await getAllClient(query);
    handleStatus(res.status, () => dispatch(logoutUser()), navigate);
    const result = await getAllProjectNames();
    handleStatus(result.status, () => dispatch(logoutUser()), navigate);

    if (res.data.ok) {
      setClients(res.data.data);
      setProjects(result?.data?.data);
      setTotalRecords(res.data.totalRecords);
      setShowLoader(false);
    }
  };

  interface ProjectLinkProps {
    projectId: string;
  }

  const ProjectLink: React.FC<ProjectLinkProps> = ({projectId}) => {
    const projectData = projects.find(
      (project: any) => project?._id === projectId
    ) as any;
    if (!projectData?.projectName) {
      return <>N/A</>;
    }
    return (
      <Link to={'/view-project/' + projectData?._id}>
        {projectData?.projectName}
      </Link>
    );
  };

  const path = localStorage.getItem('Path');
  const pageNo = localStorage.getItem('PageNo');

  const searchClients = async () => {
    getAllClients({clientName: searchRef.current.value});
  };

  useEffect(() => {
    if (path === 'clients' && pageNo !== 1) {
      setCurrentPage(pageNo);
      getAllClients({pageNo: pageNo});
    } else {
      localStorage.setItem('Path', 'clients');
      localStorage.setItem('PageNo', currentPage);
      getAllClients({});
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('PageNo', currentPage);
  }, [currentPage]);

  const paginate = (pageno) => {
    getAllClients({pageNo: pageno});
    setCurrentPage(pageno);
  };

  // if (showLoader) {
  //     return (<Loader title={"Daily"} />)
  // }
  return (
    <>
      <ContentHeader title="All Clients" />
      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">About Clients</h3>
            </div>
            <div className="card-body">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex align-items-baseline justify-content-between">
                    <h3 className="card-title card-width">All Clients</h3>
                    <div className="input-group input-width input-group-lg offset-md-2 right">
                      <input
                        ref={searchRef}
                        type="search"
                        className="form-control form-control-lg"
                        placeholder="Type your keywords"
                      />
                      <div className="input-group-append">
                        <button
                          onClick={searchClients}
                          type="submit"
                          className="btn btn-lg btn-default"
                        >
                          <i className="fa fa-search" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="card-body table-responsive p-0">
                    {showLoader ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <Loader title="Loading Clients..." />
                      </div>
                    ) : (
                      <table className="table table-striped projects">
                        <thead>
                          <tr>
                            <th style={{width: '7%'}}>#</th>
                            <th style={{width: '25%'}}>Client Name</th>
                            <th style={{width: '30%'}} className="text-center">
                              Projects
                            </th>
                            <th style={{width: '15%'}}>Status</th>
                            <th style={{width: '12%'}} className="text-center">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {clients.map((client, index) => (
                            <tr key={client._id}>
                              <td>{index + 1}</td>
                              <td>
                                <Link to={`/view-client/${client._id}`}>
                                  {client?.clientName}
                                </Link>
                                <br />
                                <small>
                                  {format(
                                    new Date(client?.created_At),
                                    'dd-MM-yyyy'
                                  )}
                                </small>
                              </td>
                              <td className="project_progress text-center">
                                {client?.project &&
                                client?.project.length > 0 ? (
                                  <div className="d-flex flex-wrap justify-content-center">
                                    {client.project.map((projectId) => (
                                      <span
                                        key={projectId}
                                        className="badge m-1"
                                      >
                                        <ProjectLink projectId={projectId} />
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span>No projects assigned</span>
                                )}
                              </td>
                              <td className="project-state text-left">
                                <Status status={client?.status} />
                              </td>
                              <td className="project-actions text-center">
                                <Link
                                  to={`/view-client/${client._id}`}
                                  className="btn btn-primary btn-sm"
                                  style={{marginRight: '5px'}}
                                >
                                  <i className="fas fa-eye" />
                                </Link>
                                <Link
                                  to={`/update-client/${client._id}`}
                                  className="btn btn-info btn-sm"
                                >
                                  <i className="fas fa-pencil-alt" />
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-pagination">
              <Pagination
                postsPerPage={postPerPage}
                totalPosts={totalRecords}
                paginate={paginate}
                currentPage={currentPage}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AllClients;
